import { Route, Routes } from 'react-router';
import { BraintreeProvider } from '@acorn-portal/shared/src/context/braintree-context';
import BackOfficePaymentSessionPage from '@acorn-portal/shared/src/pages/payment/back-office-payment-session';
import BackOfficePaymentSessionSuccessPage from '@acorn-portal/shared/src/pages/payment/BackOfficePaymentSessionSuccessPage';
import CancelledPolicy from '@acorn-portal/shared/src/pages/payNow/CancelledPolicy';
import CancelledPolicyOutstandingBalance from '@acorn-portal/shared/src/pages/payNow/CancelledPolicyOutstandingBalance';
import CancelledWithDebtPaymentConfirmation from '@acorn-portal/shared/src/pages/payNow/CancelledWithDebtPaymentConfirmation';
import DebtCollectionDirectPayment from '@acorn-portal/shared/src/pages/payNow/debt-collection-direct-payment';
import DebtCollectionPayment from '@acorn-portal/shared/src/pages/payNow/debt-collection-payment';
import DirectPayment from '@acorn-portal/shared/src/pages/payNow/direct-payment';
import DirectPaymentConfirmation from '@acorn-portal/shared/src/pages/payNow/DirectPaymentConfirmation';
import ExpiredLinkPage from '@acorn-portal/shared/src/pages/payNow/ExpiredLinkPage';
import InvalidLinkPage from '@acorn-portal/shared/src/pages/payNow/InvalidLinkPage';
import OverDueInstallmentPlan from '@acorn-portal/shared/src/pages/payNow/OverDueInstallmentPlan';
import PaymentAgreement from '@acorn-portal/shared/src/pages/payNow/payment-agreement';
import ProcessPayment from '@acorn-portal/shared/src/pages/payNow/process-payment';
import SplitPaymentAgreement from '@acorn-portal/shared/src/pages/payNow/SplitPaymentAgreement';
import TokenisedPaymentAgreement from '@acorn-portal/shared/src/pages/payNow/tokenised-payment-agreement';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import PaymentConfirmation from '@acorn/portal/src/pages/payNow/PaymentConfirmation';

/** The absolute ROUTE_PATHS constants made relative to the family wildcard these
 *  components are mounted under (`/pay-now/*` / `/payment/*`). */
const relPayNow = (path: string) => path.replace('/pay-now/', '');
const relPayment = (path: string) => path.replace('/payment/', '');

/**
 * The host's own pay-now pages, exactly as they were registered before the
 * paynowRemote swap — rendered by RemoteRouteSwitch while the flag is off.
 * Routes accept an optional :policyToken segment so tokenized URLs keep working
 * if the flag is rolled back after the link sources start appending tokens.
 */
export const LocalPayNowRoutes = () => (
	<Routes>
		<Route path={`${relPayNow(ROUTE_PATHS.paymentPlan)}/:policyToken?`} element={<PaymentAgreement />} />
		<Route path={`${relPayNow(ROUTE_PATHS.splitPaymentPlan)}/:policyToken?`} element={<SplitPaymentAgreement />} />
		<Route path={`${relPayNow(ROUTE_PATHS.paymentPlanOverdue)}/:policyToken?`} element={<OverDueInstallmentPlan />} />
		<Route
			path={`${relPayNow(ROUTE_PATHS.processPayment)}/:policyToken?`}
			element={
				<BraintreeProvider>
					<ProcessPayment />
				</BraintreeProvider>
			}
		/>
		<Route
			path={`${relPayNow(ROUTE_PATHS.payNowSPDirectPayment)}/:policyToken?`}
			element={
				<BraintreeProvider>
					<DirectPayment />
				</BraintreeProvider>
			}
		/>
		<Route path={`${relPayNow(ROUTE_PATHS.payNowConfirmation)}/:policyToken?`} element={<PaymentConfirmation />} />
		<Route
			path={`${relPayNow(ROUTE_PATHS.canxOutstandingBalance)}/:policyToken?`}
			element={<CancelledPolicyOutstandingBalance />}
		/>
		<Route path={`${relPayNow(ROUTE_PATHS.canxPayment)}/:policyToken?`} element={<DebtCollectionPayment />} />
		<Route
			path={`${relPayNow(ROUTE_PATHS.canxPaymentSuccess)}/:policyToken?`}
			element={<CancelledWithDebtPaymentConfirmation />}
		/>
		<Route path={`${relPayNow(ROUTE_PATHS.canxCancelledPolicy)}/:policyToken?`} element={<CancelledPolicy />} />
	</Routes>
);

/**
 * The host's own anonymous payment pages (token-link entry points), exactly as
 * they were registered before the paynowRemote swap. URLs are byte-identical.
 */
export const LocalPaymentRoutes = () => (
	<Routes>
		<Route
			path={relPayment(ROUTE_PATHS.payNowDirectPayment)}
			element={
				<BraintreeProvider>
					<DirectPayment />
				</BraintreeProvider>
			}
		/>
		<Route
			path={relPayment(ROUTE_PATHS.payNowDirectDebitPayment)}
			element={
				<BraintreeProvider>
					<DirectPayment />
				</BraintreeProvider>
			}
		/>
		<Route path={relPayment(ROUTE_PATHS.payNowDirectPaymentSuccess)} element={<DirectPaymentConfirmation />} />
		<Route path={relPayment(ROUTE_PATHS.canxDirectPayment)} element={<DebtCollectionDirectPayment />} />
		<Route path={relPayment(ROUTE_PATHS.canxDirectPaymentSuccess)} element={<DirectPaymentConfirmation />} />
		<Route path={relPayment(ROUTE_PATHS.expiredPaymentLink)} element={<ExpiredLinkPage />} />
		<Route path={relPayment(ROUTE_PATHS.invalidPaymentLink)} element={<InvalidLinkPage />} />
		<Route path={relPayment(ROUTE_PATHS.payNowDirectAgreement)} element={<TokenisedPaymentAgreement />} />
		<Route
			path={relPayment(ROUTE_PATHS.backOfficePaymentSession)}
			element={
				<BraintreeProvider>
					<BackOfficePaymentSessionPage />
				</BraintreeProvider>
			}
		/>
		<Route
			path={relPayment(ROUTE_PATHS.backOfficePaymentSessionSuccess)}
			element={<BackOfficePaymentSessionSuccessPage />}
		/>
	</Routes>
);
